import { differenceInDays } from 'date-fns';
import { sum } from 'lodash';
import moment from 'moment';

import { calculateCalendarMonthOffset, getMonthOffsetFromDateInMonth, getStartAndEndOfMonth } from '../../../lib/dates';
import { DATE_FORMAT_DEFAULT } from '../../../config/locale';

import * as _Types from '../../../lib/jsdocTypedefs';
import * as _AvailabilityTypes from '../../Availability/types';

/**
 * Overwrites a certain portion of an array with another
 * @param {number[]} originalArray
 * @param {number} start
 * @param {number[]} newArray
 * @returns {number[]}
 */
const replaceArraySection = (
  originalArray,
  start,
  newArray,
) => {
  if (!originalArray) return originalArray;
  const originalCopy = [...originalArray];
  // Splice runs inline so it modifies the original array
  Array.prototype.splice.apply(originalCopy, [start, newArray.length].concat(newArray));
  return originalCopy;
};

/**
 * Extracts the actual data from the Apollo API responses
 * for availability queries
 * @param {_Types.AvailabilitySearchResponse} availabilityQuery
 * @returns {_Types.AvailabilityType}
 */
const parseAvailabilityQuery = (availabilityQuery) => availabilityQuery?.data?.availability ?? {};

/**
 * Gets the currently selected pitch from an availability response
 * @param {_Types.AvailabilitySearchResponse} availabilityQuery
 * @param {string} pitchCode
 * @returns {_Types.Pitch}
 */
const getPitchFromAvailability = (availabilityQuery, pitchCode) => {
  const parsedAvailability = parseAvailabilityQuery(availabilityQuery);
  return parsedAvailability?.pitchTypes?.find((availPitch) => availPitch.code === pitchCode) ?? {};
};

/**
 * When a user books a pitch, the prices they have paid are saved against their booking. When they
 * then come back to amend, we place these prices at the correct places in the calendar so they do
 * not get confused when shown pricing which has potentially changed since booking.
 *
 * This helper performs some manipulation on the AvailabilitySearch CPN true response to place the
 * booked nightly prices in their correct indexes, even across month boundaries.
 *
 * @param {_Types.BookingDetails} booking
 * @param {_AvailabilityTypes.AvailabilitySearchResponse} availabilitySearch
 * @param {_Types.Pitch} pitch
 * @param {string[]} calendarDates
 * @param {string} amendedComponentId
 * @param {boolean} hasUpdatedComponents
 * @returns {_Types.Pitch[]}
 */
export const mapNightlyPricesToAmendsAvailability = (
  booking,
  availabilitySearch,
  pitch,
  calendarDates,
  amendedComponentId,
  hasUpdatedComponents,
) => {
  if (!availabilitySearch) return null;

  let pitchesWithPricing = [...availabilitySearch];

  if (!booking || !amendedComponentId) return pitchesWithPricing;

  const bookingSite = booking.campsiteBookings.find(
    (campsite) => campsite.id === amendedComponentId,
  );

  if (!bookingSite) return pitchesWithPricing;

  const bookingPitch = bookingSite.pitches.find((bookedPitch) => bookedPitch.code === pitch.code);

  if (!bookingPitch) return pitchesWithPricing;

  pitchesWithPricing = pitchesWithPricing.map((pitchWithPrice) => {
    const copiedPitchWithPrice = { ...pitchWithPrice };

    // If this isn't the booked pitch, just return it as-is
    if (pitchWithPrice.code !== pitch.id && pitchWithPrice.code !== pitch.code) {
      return pitchWithPrice;
    }

    const { nightlyPrices } = bookingPitch;

    // And if the booking has no nightly prices on the pitch, return as-is
    if (!nightlyPrices) return pitchWithPrice;

    // Get all nightly prices that are present in the current month. This helps cope
    // with bookings that cross month boundaries
    // This also uses the visible calendar cells rather than the 'month', as a 'month' on
    // the calendar can be up to 42 days.
    const presentPrices = nightlyPrices.filter(
      (nightlyPriceWithDate) => calendarDates.findIndex(
        (calendarDate) => calendarDate === nightlyPriceWithDate.date,
      ) !== -1,
    );

    // If there's no nightly prices for this month, return the pricing from the API
    if (!presentPrices?.length > 0) return pitchWithPrice;

    // Finds the index where our nightly prices should start to be mapped
    const firstPriceIndex = calendarDates.findIndex((date) => date === presentPrices[0].date);

    // Convert the array of dates and prices to just an array of prices, to be mapped
    // on to existing availability.
    const simplifiedPrices = presentPrices.map((priceWithDate) => priceWithDate.price);

    // Now we need to change the prices array to have the booked prices on the booked dates
    // for both members and non-members
    const { memberAvailability, nonMemberAvailability } = copiedPitchWithPrice;

    const pricesAreForThisMonth = firstPriceIndex !== -1;
    const hasPrices = simplifiedPrices?.length > 0;

    // If the user has not changed anything critical to quote price AND we have prices and they are
    // for this month, then replace the returned prices with the nightly prices from the user's
    // booking.
    if (!hasUpdatedComponents && pricesAreForThisMonth && hasPrices) {
      const updatedMemberPrices = replaceArraySection(
        memberAvailability,
        firstPriceIndex,
        simplifiedPrices,
      );

      const updatedNonMemberPrices = replaceArraySection(
        nonMemberAvailability,
        firstPriceIndex,
        simplifiedPrices,
      );

      copiedPitchWithPrice.memberAvailability = updatedMemberPrices;
      copiedPitchWithPrice.nonMemberAvailability = updatedNonMemberPrices;
    }

    return copiedPitchWithPrice;
  });

  return pitchesWithPricing;
};

/**
 * Calculates the total stay price from the availability search
 * and total requests
 * @param {_Types.AvailabilitySearchResponse} availabilityTotal
 * @param {_Types.AvailabilitySearchResponse} availabilitySearch
 * @param {{
 *  start: string,
 *  end: string,
 * }} selectedDates
 * @param {_Types.Pitch} pitch
 * @returns {number} totalPitchPrice
 */
export const calculateTotalPriceFromAvailability = (
  availabilityTotal,
  availabilitySearch,
  selectedDates,
  pitch,
  isMember,
) => {
  const availabilityTotalPitch = getPitchFromAvailability(availabilityTotal, pitch.id);
  // Array to track the nightly prices which is summed at the end
  // to get the total stay price
  let nightlyPrices = [];
  const totalPriceKey = isMember ? 'memberAvailabilityTotalPrice' : 'nonMemberAvailabilityTotalPrice';

  const hasTotalPrice = availabilityTotalPitch &&
    availabilityTotalPitch?.code &&
    availabilityTotalPitch?.[totalPriceKey];

  // If we have valid prices on availabilityTotal use that. This price includes
  // offers, etc.
  if (hasTotalPrice) return availabilityTotalPitch?.[totalPriceKey];

  const nightyPricesKey = isMember ? 'memberAvailability' : 'nonMemberAvailability';

  // If we don't have prices in availabilityTotal, get them from availabilitySearch
  const { start: startDate, end: endDate } = selectedDates;
  const { start: startOfMonth } = getStartAndEndOfMonth(startDate);
  const calendarStartOffset = calculateCalendarMonthOffset(startOfMonth);
  const startIndex =
    Number(startDate?.substring(8, 10) ?? 0) + calendarStartOffset - 1;
  const availabilitySearchPitch = getPitchFromAvailability(
    availabilitySearch,
    pitch.id,
  );
  const allPrices = availabilitySearchPitch[nightyPricesKey];
  if (allPrices?.length > 0) {
    const stayLength = Math.abs(differenceInDays(startDate, endDate));
    const relevantPrices = allPrices.slice(startIndex, startIndex + stayLength);
    nightlyPrices = relevantPrices;
  }

  return sum(nightlyPrices);
};

/**
 * Returns the index for a date in the 42-day availability we get from the BE
 * (starting at 0).
 * @param {string} startDate Date in the format: YYYY-MM-DD
 * @returns {number} The index of the current date in an availability array
 */
export const getAvailabilityIndexForDate = (startDate) => {
  // Find how many days are shown on the calendar, before the first of the month.
  const calendarStartOffset = getMonthOffsetFromDateInMonth(startDate);
  // Returns the index of the start date, with the calendar start offset included
  // minus one so we start at 0.
  return calendarStartOffset + moment(startDate, DATE_FORMAT_DEFAULT).date() - 1;
};

export default mapNightlyPricesToAmendsAvailability;
